import { useAuth0 } from '@auth0/auth0-react';
import isBrowser from '@hellodarwin/core/lib/features/helpers/is-browser';
import { useNavigate } from '@hellodarwin/core/lib/features/providers/location-provider';
import { useCallback, useEffect, useState } from 'react';
import { resetSegmentAnalytics } from '../../segment-provider';
const INTERACTIVE_AUTH_ERRORS = [
    'consent_required',
    'interaction_required',
    'login_required',
];
const isInteractiveAuthError = (err) => INTERACTIVE_AUTH_ERRORS.includes((err === null || err === void 0 ? void 0 : err.error) || '');
const getCurrentPath = () => {
    if (!isBrowser)
        return '';
    return window.location.pathname + (window.location.search || '');
};
const getResolvedRedirectUrl = (redirectUrl) => {
    if (!isBrowser)
        return redirectUrl;
    if (redirectUrl.startsWith('?')) {
        return `${window.location.pathname}${redirectUrl}`;
    }
    return redirectUrl;
};
const useHandleLogin = (customTokenStorageKey, localStorageKeys) => {
    const [accessToken, setAccessToken] = useState('');
    const [hasTriedLogin, setHasTriedLogin] = useState(false);
    const { loginWithRedirect, logout, isAuthenticated, isLoading, getAccessTokenSilently, user, } = useAuth0();
    const navigate = useNavigate();
    useEffect(() => {
        if (!isBrowser)
            return;
        const currentPath = getCurrentPath();
        const isAuthenticatedOrLoading = isAuthenticated || isLoading;
        const withParams = currentPath.includes('?');
        const isOrigin = ['/', '/signup'].includes(window.location.pathname);
        const isOriginWithoutParams = !withParams && isOrigin;
        const shouldOverride = !isAuthenticatedOrLoading && !isOriginWithoutParams;
        if (shouldOverride) {
            if (isOrigin) {
                localStorage.setItem('redirectUrl', window.location.search);
            }
            else {
                localStorage.setItem('redirectUrl', currentPath);
            }
        }
    }, [
        isAuthenticated,
        isLoading,
    ]);
    const handleLoginWithRedirect = useCallback(async (authorizationParams) => {
        if (!isBrowser)
            return;
        const currentPath = getCurrentPath();
        const params = new URLSearchParams(window.location.search);
        const email = params.get('email');
        await loginWithRedirect({
            appState: {
                returnTo: currentPath,
            },
            authorizationParams: Object.assign({ screen_hint: window.location.pathname === '/signup' ? 'signup' : undefined, login_hint: email || undefined }, authorizationParams),
        });
    }, [loginWithRedirect]);
    const handleLogout = useCallback(() => {
        if (!isBrowser)
            return;
        localStorageKeys === null || localStorageKeys === void 0 ? void 0 : localStorageKeys.forEach((key) => localStorage.removeItem(key));
        localStorage.setItem('redirectUrl', getCurrentPath());
        resetSegmentAnalytics();
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    }, [localStorageKeys, logout]);
    const refreshAccessToken = useCallback(async () => {
        try {
            const refreshedAccessToken = await getAccessTokenSilently({
                cacheMode: 'off',
            });
            setAccessToken(refreshedAccessToken);
            if (isBrowser) {
                localStorage.setItem(customTokenStorageKey, refreshedAccessToken);
            }
            return refreshedAccessToken;
        }
        catch (err) {
            if (isInteractiveAuthError(err)) {
                await handleLoginWithRedirect();
                return undefined;
            }
            throw err;
        }
    }, [customTokenStorageKey, getAccessTokenSilently, handleLoginWithRedirect]);
    useEffect(() => {
        if (isLoading || hasTriedLogin)
            return;
        setHasTriedLogin(true);
        (async () => {
            if (!isAuthenticated) {
                await handleLoginWithRedirect();
                return;
            }
            try {
                const accessToken = await getAccessTokenSilently();
                if (accessToken) {
                    setAccessToken(accessToken);
                    if (isBrowser) {
                        const currentPath = getCurrentPath();
                        localStorage.setItem(customTokenStorageKey, accessToken);
                        const redirectUrl = localStorage.getItem('redirectUrl');
                        if (redirectUrl) {
                            localStorage.removeItem('redirectUrl');
                            const resolvedRedirectUrl = getResolvedRedirectUrl(redirectUrl);
                            if (resolvedRedirectUrl !== currentPath) {
                                navigate(resolvedRedirectUrl);
                            }
                        }
                    }
                }
            }
            catch (err) {
                console.error('Error fetching access token', err);
                if (isInteractiveAuthError(err)) {
                    await handleLoginWithRedirect();
                }
            }
        })();
    }, [
        getAccessTokenSilently,
        handleLoginWithRedirect,
        isLoading,
        isAuthenticated,
        hasTriedLogin,
        customTokenStorageKey,
        navigate,
    ]);
    const forceReauthentication = useCallback(async () => handleLoginWithRedirect({
        prompt: 'login',
        max_age: 0,
    }), [handleLoginWithRedirect]);
    return {
        forceReauthentication,
        handleLogout,
        refreshAccessToken,
        accessToken,
        isLoading,
        user,
    };
};
export default useHandleLogin;
